import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Switch, message } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi, PermissionTreeView } from '@zero/auth-admin-ui';
import { useEnabledModules } from '@fms/shared';
import { useGridPageSize, GRID_PAGE_SIZE_OPTIONS } from '../../hooks/useGridPageSize.js';

// Module quyền (PermissionDto.Module) -> FeKey của module (khớp /api/modules). Auth = lõi, luôn hiện.
// PHẢI khai đủ MỌI nhóm quyền: thiếu mã nào thì mã đó lọt lưới (fe = undefined -> luôn hiện),
// tức tắt module vẫn thấy quyền của nó. FMS.PURC (Mua sắm) nằm trong SPA 'pms' nên dùng chung FeKey.
const MODULE_FEKEY = {
  'FMS.MAST': 'master',
  'FMS.NRPT': 'noon',
  'FMS.PMSV': 'pms',
  'FMS.PURC': 'pms',
  'FMS.DLOG': 'dlog',
};

export function RolesPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const enabledModules = useEnabledModules();   // string[] | null (null = chưa tải -> hiện hết)
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allPerms, setAllPerms] = useState([]);
  const [editing, setEditing] = useState(null);   // { RoleID?, Name, Description, Require2FA, IsBuiltIn }
  const [editPerms, setEditPerms] = useState([]);   // quyền HIỂN THỊ (module đang bật) mà tree quản lý
  const [hiddenHeld, setHiddenHeld] = useState([]); // quyền role ĐANG CÓ nhưng thuộc module tắt -> giữ nguyên, không strip
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useGridPageSize('roles', 25);

  // Ẩn quyền của module bị disable (giữ Auth + module đang bật). Chưa tải enabled -> hiện hết.
  const visiblePerms = useMemo(() => {
    if (!enabledModules) return allPerms;
    return allPerms.filter((p) => {
      const fe = MODULE_FEKEY[p.Module];
      return !fe || enabledModules.includes(fe);
    });
  }, [allPerms, enabledModules]);

  const refresh = () => {
    setLoading(true);
    api.listRoles({ offset: 0, limit: 100 })
      .then((r) => setRoles(r.data?.Data?.Items ?? []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); api.listPermissions().then((r) => setAllPerms(r.data?.Data ?? [])); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setEditing({ Name: '', Description: '', Require2FA: false }); setEditPerms([]); setHiddenHeld([]); };
  const openEdit = async (role) => {
    setEditing(role); setEditPerms([]); setHiddenHeld([]);
    try {
      const r = await api.getRolePermissions(role.RoleID);
      const ids = (r.data?.Data ?? []).map((p) => (typeof p === 'object' ? p.PermissionID : p));
      const visibleIds = new Set(visiblePerms.map((p) => p.PermissionID));
      setEditPerms(ids.filter((id) => visibleIds.has(id)));       // tree quản lý phần đang bật
      setHiddenHeld(ids.filter((id) => !visibleIds.has(id)));     // giữ quyền module tắt (không strip khi lưu)
    } catch { /* để trống nếu lỗi */ }
  };

  const save = async () => {
    if (!editing?.Name?.trim()) { message.warning(t('role.nameRequired', 'Nhập tên vai trò')); return; }
    setSaving(true);
    try {
      const dto = { Name: editing.Name.trim(), Description: editing.Description || '', Require2FA: !!editing.Require2FA };
      let roleId = editing.RoleID;
      if (roleId) await api.updateRole(roleId, dto);
      else { const r = await api.createRole(dto); roleId = r.data?.Data?.RoleID ?? r.data?.Data?.roleID; }
      // Gộp lại quyền module-tắt đang có để KHÔNG bị mất khi lưu.
      if (roleId) await api.setRolePermissions(roleId, [...new Set([...editPerms, ...hiddenHeld])]);
      message.success(t('common.saved', 'Đã lưu'));
      setEditing(null); refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally { setSaving(false); }
  };

  const columns = [
    { title: t('common.id'),          dataIndex: 'RoleID',      width: 80 },
    { title: t('common.name'),        dataIndex: 'Name',        render: (v) => <strong>{v}</strong> },
    { title: t('common.description'), dataIndex: 'Description', ellipsis: true },
    { title: '2FA',                   dataIndex: 'Require2FA',  width: 80,  render: (v) => v ? <Tag color="orange">{t('common.yes')}</Tag> : '-' },
    { title: t('role.builtIn'),       dataIndex: 'IsBuiltIn',   width: 100, render: (v) => v ? <Tag>Built-in</Tag> : '-' },
    { title: t('common.actions'),     key: 'a', width: 100, render: (_, r) => (
      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
    ) },
  ];

  return (
    <Card size="small" extra={
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreate}>
        {t('common.createNew', 'Tạo mới')}
      </Button>
    }>
      <Table dataSource={roles} columns={columns} loading={loading} rowKey="RoleID" size="small" bordered
             pagination={{
               pageSize,
               showSizeChanger: true,
               pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
               onShowSizeChange: (_, size) => setPageSize(size),
             }}
             scroll={{ y: 'calc(100vh - 200px)' }} />

      <Modal open={!!editing} onCancel={() => setEditing(null)} onOk={save} confirmLoading={saving}
             title={editing?.RoleID ? `${t('common.edit', 'Sửa')} — ${editing?.Name}` : t('role.createTitle', 'Tạo vai trò mới')}
             width={1000} destroyOnClose maskClosable={false}>
        <Form layout="vertical" style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item label={t('common.name', 'Tên vai trò')} required style={{ flex: 1, marginBottom: 8 }}>
              <Input value={editing?.Name ?? ''} disabled={editing?.IsBuiltIn}
                     onChange={(e) => setEditing((s) => ({ ...s, Name: e.target.value }))}
                     placeholder={t('role.namePh', 'VD: Kỹ thuật viên tàu')} />
            </Form.Item>
            <Form.Item label={t('role.require2fa', 'Bắt buộc 2FA')} style={{ marginBottom: 8 }}>
              <Switch checked={!!editing?.Require2FA} onChange={(v) => setEditing((s) => ({ ...s, Require2FA: v }))} />
            </Form.Item>
          </div>
          <Form.Item label={t('common.description', 'Mô tả')} style={{ marginBottom: 8 }}>
            <Input value={editing?.Description ?? ''}
                   onChange={(e) => setEditing((s) => ({ ...s, Description: e.target.value }))} />
          </Form.Item>
        </Form>
        <PermissionTreeView permissions={visiblePerms} selectedIds={editPerms} onChange={setEditPerms} />
      </Modal>
    </Card>
  );
}
